import Axios from 'axios';

import '../styles/components/popup.scss';

import alert from '../components/alert/index';
import { catchApiList, showSessionTimeoutModal } from '../utils/index';
import { poorConnectionBanner } from '../components/alert/popAlert';
import { v4 as uuidv4 } from 'uuid';
import {
  markAsAxiosRestFailure,
  parseRestApiError,
} from '../errors/restApiError';
import { reportApiFailure } from '../errors/errorReporter';
import {
  resolveRestErrorPolicy,
  PolicyAction,
} from '../errors/restErrorPolicies';
import { showRestError } from '../errors/globalFeedbackBridge';

const baseURL = process.env.REACT_APP_service_url;

const axios = Axios.create({
  baseURL,
  withCredentials: true,
  timeout: 60000 * 5,
});

const HTTP_CODE = {
  CREATED: 201,
};

const appendAuth = (config) => {
  const token = window.localStorage.getItem('accessToken');
  const refreshToken = window.localStorage.getItem('refreshToken');
  config.headers.authorization = token;
  config.headers.refreshToken = refreshToken;
  config.headers['auth-origin'] = origin;
};

const catchProjectIdIsNull = (config) => {
  const url = config.url || '';
  const prefix = catchApiList.find((item) => url.startsWith(item));
  if (!prefix) {
    return;
  }
  const projectId = url.split(prefix)?.[1];
  if (!projectId || ['undefined', 'null'].includes(projectId)) {
    axios.post('/api/v1/email/manualReport', {
      url: window.location.href,
      apiUrl: config.url,
      requestHeaders: JSON.stringify(config.headers || {}),
      requestParams: JSON.stringify(config.data || {}),
    });
  }
};

function skipGlobalFeedback(config) {
  return Boolean(
    config?.skipGlobalErrorHandler || config?.ddv?.skipGlobalErrorHandler,
  );
}

function handleAxiosResponseError(error, { baseURL: apiBase }) {
  window.SessionStorage.removeItem('loginProgress');
  const cfg = error?.config || {};

  if (
    error?.message === 'canceled' ||
    Axios.isCancel(error) ||
    error?.code === 'ERR_CANCELED'
  ) {
    const silent = markAsAxiosRestFailure(parseRestApiError(error));
    return Promise.reject(silent);
  }

  const policy = resolveRestErrorPolicy({
    error,
    baseURL: apiBase,
  });

  if (policy.action === PolicyAction.REJECT_RAW) {
    return Promise.reject(error);
  }

  const apiClientError = parseRestApiError(error);

  const skipUi = skipGlobalFeedback(cfg);

  const shouldReport =
    policy.action !== PolicyAction.SILENT &&
    policy.action !== PolicyAction.REJECT_RAW;

  if (shouldReport) {
    reportApiFailure({ apiClientError, axiosError: error });
  }

  markAsAxiosRestFailure(apiClientError);

  switch (policy.action) {
    case PolicyAction.SILENT:
      return Promise.reject(apiClientError);
    case PolicyAction.REDIRECT_GATEWAY:
      window.location.href = policy.redirectHref || '/error?requestApp=Gateway';
      return Promise.reject(apiClientError);
    case PolicyAction.SESSION:
      showSessionTimeoutModal();
      return Promise.reject(apiClientError);
    case PolicyAction.USER_ALREADY_REGISTERED:
      if (!skipUi) {
        alert.error({
          content: 'This email has already been registered!',
        });
      }
      return Promise.reject(apiClientError);
    case PolicyAction.NETWORK_BANNER:
      if (!skipUi) {
        // eslint-disable-next-line no-console
        console.log({ error });
        poorConnectionBanner.alert();
      }
      return Promise.reject(apiClientError);
    case PolicyAction.SHOW:
    default:
      if (!skipUi) {
        showRestError(apiClientError);
      }
      return Promise.reject(apiClientError);
  }
}

axios.interceptors.request.use(
  (config) => {
    catchProjectIdIsNull(config);
    if (config.url.startsWith('/')) {
      appendAuth(config);
      config.headers.page = window.location.pathname + window.location.search;
      config.headers.requestId = uuidv4();
    }

    return config;
  },
  (err) => Promise.reject(err),
);

axios.interceptors.response.use(
  (response) => {
    const result = response?.data?.data || response?.data || {};

    if (response.headers?.token) {
      window.localStorage.setItem('accessToken', response.headers.token);
      window.localStorage.accessToken = response.headers.token;
    }
    if (response.headers?.refreshtoken) {
      window.localStorage.setItem(
        'refreshToken',
        response.headers.refreshtoken,
      );
      window.localStorage.refreshToken = response.headers.refreshtoken;
    }

    if (
      response.headers['content-disposition'] &&
      response.headers['content-disposition'].includes('filename')
    ) {
      return {
        data: response.data,
        fileName: response.headers['content-disposition']
          .split('filename=')[1]
          .replace(/"/g, ''),
      };
    }

    if (response.config?.includeResponseHeaders) {
      return {
        data: response.data,
        headers: response.headers || {},
      };
    }

    if (response.status === HTTP_CODE.CREATED) {
      result.success = result?.status === 'SUCCESS';
    }

    return result;
  },
  (error) => handleAxiosResponseError(error, { baseURL }),
);

const authBaseURL = process.env.REACT_APP_NEW_LOGIN_AUTH_URL;

const authAxios = Axios.create({
  baseURL: authBaseURL,
  withCredentials: true,
  timeout: 60000 * 5,
});

authAxios.interceptors.request.use(
  (config) => {
    appendAuth(config);
    config.headers.page = window.location.pathname + window.location.search;
    config.headers.requestId = uuidv4();
    return config;
  },
  (err) => Promise.reject(err),
);

authAxios.interceptors.response.use(
  (response) => {
    const result = response?.data?.data || response?.data || {};

    if (response.headers?.token) {
      window.localStorage.setItem('accessToken', response.headers.token);
    }
    if (response.headers?.refreshtoken) {
      window.localStorage.setItem(
        'refreshToken',
        response.headers.refreshtoken,
      );
    }

    return result;
  },
  (error) => handleAxiosResponseError(error, { baseURL }),
);

export { axios, appendAuth, authAxios };
